import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert, Button, ConfirmDialog, DataGrid, useToast, type DataGridColumn,
} from '@kss/ui';
import { Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { productService, type Product } from '@kss/api';
import { encodeId } from '../../utils/idCodec';
import { AuthImg } from '../../components/AuthImg';
import { NameCell } from '../../hooks/useNameMaps';

export default function AllProducts() {
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast); toastRef.current = toast;

  const [rows, setRows] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Product | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function load() {
    setLoading(true); setErr(null);
    try {
      const data = await productService.all();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load';
      setErr(msg); toastRef.current.error('Load failed', msg);
    } finally { setLoading(false); }
  }
  useEffect(() => { void load(); }, []);

  async function onDelete() {
    if (!confirmDelete?.productId) return;
    setDeleting(true);
    try {
      await productService.delete(confirmDelete.productId);
      toast.success('Deleted', `${confirmDelete.name} removed`);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error('Delete failed', e instanceof Error ? e.message : 'Failed');
    } finally { setDeleting(false); }
  }

  const columns: DataGridColumn<Product>[] = [
    {
      accessor: 'imageUrl',
      title: '',
      width: 60,
      render: (r) =>
        r.imageUrl ? (
          <AuthImg src={String(r.imageUrl)} className="h-9 w-9 rounded border border-border object-cover" />
        ) : (
          <div className="flex h-9 w-9 items-center justify-center rounded border border-dashed border-border text-[10px] text-content-muted">—</div>
        ),
    },
    { accessor: 'name', title: 'Name', sortable: true, filterable: true },
    { accessor: 'sku', title: 'SKU', sortable: true, filterable: true, width: 140 },
    {
      accessor: 'categoryId',
      title: 'Category',
      width: 160,
      render: (r) => <NameCell kind="category" id={r.categoryId} />,
    },
    {
      accessor: 'sellingPrice',
      title: 'Sell price',
      width: 110,
      // §35: schema renamed sellingPrice → price. Read both.
      render: (r) => {
        const v = (r as { price?: number; sellingPrice?: number; mrp?: number }).price
          ?? r.sellingPrice ?? (r as { mrp?: number }).mrp;
        return v != null ? v.toLocaleString('en-IN', { style: 'currency', currency: 'INR' }) : '—';
      },
    },
    {
      accessor: 'taxRate',
      title: 'Tax',
      width: 80,
      render: (r) => (r.taxRate != null ? `${r.taxRate}%` : '—'),
    },
    {
      accessor: 'hasVariants',
      title: 'Variants',
      width: 90,
      render: (r) => r.hasVariants ? (r.variants?.length ?? 0) : '—',
    },
    {
      accessor: 'isActive',
      title: 'Status',
      width: 100,
      render: (r) => (
        <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${r.isActive ? 'bg-success/10 text-success' : 'bg-content-muted/10 text-content-muted'}`}>
          {r.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      accessor: 'productId',
      title: 'Actions',
      width: 110,
      render: (r) => (
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={() => r.productId && navigate(`/panel/products/${encodeId(r.productId)}`)} title="Edit">
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(r)} title="Delete">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="-m-6 p-[3px]">
      {err && <Alert variant="danger" className="mb-3">{err}</Alert>}
      <DataGrid<Product>
        title={<span className="hidden sm:inline">Products</span>}
        data={rows}
        columns={columns}
        defaultPageSize={25}
        pageSizes={[10, 25, 50, 100, 250]}
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Link to="/panel/products/new">
              <Button size="sm"><Plus className="h-3.5 w-3.5" /> New product</Button>
            </Link>
          </div>
        }
      />
      <ConfirmDialog
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={onDelete}
        title="Delete product?"
        message={`Permanently remove "${confirmDelete?.name ?? ''}"? This cannot be undone.`}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        variant="danger"
      />
    </div>
  );
}
